import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, Users, CreditCard, Brain, Shield, Download } from 'lucide-react';
import { useReducedMotion } from '../hooks/useReducedMotion';

interface TermsOfServiceProps {
  onAccept?: () => void;
  isAccepted?: boolean;
}

const sections = [
  {
    id: 'acceptable-use',
    title: 'Acceptable Use',
    icon: Users,
    content: [
      {
        subtitle: 'Community Guidelines',
        items: [
          'Treat all community members with respect and kindness',
          'Share experiences constructively without judgment',
          'Respect privacy - never share personal details of others',
          'Report inappropriate content or behavior immediately'
        ]
      },
      {
        subtitle: 'AI Content Usage',
        items: [
          'AI suggestions are tools for reflection, not professional advice',
          'Do not rely solely on AI recommendations for major life decisions',
          'Understand that AI responses are based on patterns, not personal knowledge',
          'Seek professional help for serious relationship or mental health concerns'
        ]
      }
    ]
  },
  {
    id: 'subscription-terms',
    title: 'Subscription Terms',
    icon: CreditCard,
    content: [
      {
        subtitle: 'Plan Details',
        items: [
          'Basic: Free tier with core relationship insights',
          'Pro ($9.99/month): Advanced AI features and VR role-play',
          'Premium ($19.99/month): Full access including wearable integration',
          'All plans include 14-day free trial for new users'
        ]
      },
      {
        subtitle: 'Billing & Refunds',
        items: [
          'Subscriptions auto-renew unless cancelled 24 hours before renewal',
          'Refunds available within 7 days of purchase for unused periods',
          'Cancellation takes effect at the end of current billing cycle',
          'No refunds for partial months or unused features'
        ]
      }
    ]
  },
  {
    id: 'ai-disclaimer',
    title: 'AI Content Disclaimer',
    icon: Brain,
    content: [
      {
        subtitle: 'AI-Generated Advice',
        items: [
          'Gemini AI provides suggestions based on relationship patterns and research',
          'AI advice is not a substitute for professional counseling or therapy',
          'Individual results may vary - what works for others may not work for you',
          'Always use your judgment when applying AI recommendations'
        ]
      },
      {
        subtitle: 'Accuracy & Limitations',
        items: [
          'AI insights are based on available data and may not capture full context',
          'Wearable data correlations are estimates, not medical diagnoses',
          'VR scenarios are simulations and may not reflect real-world outcomes',
          'We continuously improve AI accuracy but cannot guarantee perfection'
        ]
      }
    ]
  },
  {
    id: 'liability',
    title: 'Liability Limitation',
    icon: Shield,
    content: [
      {
        subtitle: 'Service Limitations',
        items: [
          'Octify is a relationship improvement tool, not professional counseling',
          'We do not guarantee specific relationship outcomes or improvements',
          'Technical issues may occasionally affect service availability',
          'Third-party integrations (wearables, AI) may have their own limitations'
        ]
      },
      {
        subtitle: 'User Responsibility',
        items: [
          'You are responsible for how you apply insights and suggestions',
          'Seek professional help for serious relationship or mental health issues',
          'Verify important decisions with trusted friends, family, or professionals',
          'Use the app as one tool among many for relationship growth'
        ]
      }
    ]
  }
];

export const TermsOfService: React.FC<TermsOfServiceProps> = ({ onAccept, isAccepted = false }) => {
  const [showPDF, setShowPDF] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  const handleViewPDF = () => {
    // In a real app, this would open a PDF version
    console.log('Opening PDF version of Terms of Service');
    setShowPDF(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-blue-50 to-teal-100 dark:from-purple-900/20 dark:via-blue-900/20 dark:to-teal-900/20 pt-20">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute inset-0 opacity-30"
          animate={prefersReducedMotion ? {} : {
            background: [
              "radial-gradient(circle at 20% 80%, #8B5CF6 0%, transparent 50%)",
              "radial-gradient(circle at 80% 20%, #06B6D4 0%, transparent 50%)",
              "radial-gradient(circle at 40% 40%, #10B981 0%, transparent 50%)",
            ]
          }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        
        {/* Shimmer Effect */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"
          animate={prefersReducedMotion ? {} : {
            x: ['-100%', '100%']
          }}
          transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
        />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full mb-6">
            <FileText className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Terms of Service
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Clear guidelines for using Octify responsibly and getting the most from our AI-powered relationship insights.
          </p>
        </motion.div>

        {/* Key Points */}
        <motion.div
          className="mb-12 p-8 rounded-3xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-xl border border-white/20 dark:border-gray-700/20"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
            Key Points
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                icon: Brain,
                title: 'AI Guidance',
                description: 'AI suggestions are tools for reflection, not professional advice.'
              },
              {
                icon: Users,
                title: 'Community Respect',
                description: 'Treat all members with kindness and respect privacy boundaries.'
              },
              {
                icon: CreditCard,
                title: 'Fair Billing',
                description: '14-day free trial, easy cancellation, and transparent pricing.'
              },
              {
                icon: Shield,
                title: 'Responsible Use',
                description: 'Use Octify as one tool among many for relationship growth.'
              }
            ].map((point, index) => (
              <motion.div
                key={point.title}
                className="flex items-start gap-4"
                initial={prefersReducedMotion ? {} : { opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
              >
                <div className="inline-flex items-center justify-center w-10 h-10 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex-shrink-0">
                  <point.icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {point.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    {point.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Detailed Sections */}
        <div className="space-y-8">
          {sections.map((section, sectionIndex) => (
            <motion.div
              key={section.id}
              className="p-8 rounded-3xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-xl border border-white/20 dark:border-gray-700/20"
              initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: sectionIndex * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full">
                  <section.icon className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {section.title}
                </h2>
              </div>

              <div className="space-y-6">
                {section.content.map((subsection, subsectionIndex) => (
                  <motion.div
                    key={subsection.subtitle}
                    initial={prefersReducedMotion ? {} : { opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: subsectionIndex * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      {subsection.subtitle}
                    </h3>
                    <ul className="space-y-2">
                      {subsection.items.map((item, itemIndex) => (
                        <motion.li
                          key={item}
                          className="flex items-start gap-3"
                          initial={prefersReducedMotion ? {} : { opacity: 0, x: -10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.4, delay: itemIndex * 0.05 }}
                          viewport={{ once: true }}
                        >
                          <div className="w-2 h-2 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex-shrink-0 mt-2" />
                          <span className="text-gray-600 dark:text-gray-300">{item}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* PDF Download */}
        <motion.div
          className="mt-12 p-8 rounded-3xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-xl border border-white/20 dark:border-gray-700/20 text-center"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Need a Copy?
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Download a PDF version of our Terms of Service for your records.
          </p>
          <motion.button
            onClick={handleViewPDF}
            className="px-6 py-3 bg-white/10 dark:bg-gray-800/10 text-gray-900 dark:text-white rounded-full font-medium hover:bg-white/20 dark:hover:bg-gray-800/20 transition-colors duration-200 flex items-center gap-2 mx-auto"
            whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
            whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
          >
            <Download className="w-5 h-5" />
            View PDF Version
          </motion.button>
        </motion.div>

        {/* Last Updated */}
        <div className="mt-12 text-center text-sm text-gray-500 dark:text-gray-400">
          Last updated: January 2025 • Version 1.0
        </div>
      </div>
    </div>
  );
};